import { forwardRef, useId, useImperativeHandle, useRef, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { FIELD_SLOT_CLASSES } from '@/shared/components/ui/field/field.tokens';
import { Slider } from '@/shared/components/ui/Slider/Slider';
import { cn } from '@/shared/utils/cn/cn.util';

import { ScalarFieldIsland } from '../ScalarFieldIsland/ScalarFieldIsland';
import { ScalarHint } from '../ScalarHint/ScalarHint';
import { ScalarShell } from '../ScalarShell/ScalarShell';
import {
  PERCENT_AMOUNT_CLASSES,
  PERCENT_SLIDER_CLASSES,
  SCALAR_ADORNMENT_CLASSES,
} from '../scalar.tokens';
import { type ScalarHandle } from '../types/scalar-field.types';

import { type PercentInputProps } from './PercentInput.types';
import { usePercentInputLogic } from './usePercentInputLogic';

/** What one press of each key is worth, as a multiple of `step`. */
const KEY_STEPS: Readonly<Record<string, number>> = {
  ArrowUp: 1,
  ArrowDown: -1,
  PageUp: 10,
  PageDown: -10,
};

/**
 * A 0–1 threshold, edited as a percentage.
 *
 * **Not self-naming.** It renders exactly one value control, so the caller's
 * `<Label htmlFor>` names it and this component must not set an `aria-label` —
 * an `aria-label` wins over a visible label and would hide it from the very
 * readers it was meant to serve. The slider beside it takes a composed name of
 * its own so the two are told apart.
 *
 * The slider is not decoration: 0.85 in a text box says nothing about how strict
 * it is, and a bounded range is exactly the question an operator is answering.
 * Both controls drive one value through one refusal choke point.
 */
export const PercentInput = forwardRef<ScalarHandle, PercentInputProps>(function PercentInput(
  {
    id,
    className,
    size,
    label,
    value,
    onValueChange,
    min,
    max,
    decimals,
    step = 1,
    isDisabled = false,
    isReadOnly = false,
    isInvalid = false,
    onBlur,
    'aria-describedby': describedBy,
  },
  ref,
) {
  const { t } = useTranslation('uiKit');
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Generated, never derived from a literal: two id-less percent fields on one
  // page both minted `percent-hint`, so every field after the first was
  // described by the FIRST one's stored value.
  const generatedId = useId();
  const hintId = `${id ?? generatedId}-hint`;

  const view = usePercentInputLogic({
    label,
    value,
    onValueChange,
    min,
    max,
    decimals,
    step,
    isDisabled,
    isReadOnly,
  });

  useImperativeHandle(ref, () => ({
    focus: () => inputRef.current?.focus(),
    get element() {
      return inputRef.current;
    },
  }));

  return (
    <ScalarShell
      className={className}
      label={label}
      isReadOnly={isReadOnly}
      announcement={view.announcement}
      hint={
        <ScalarHint id={hintId} range={view.rangeHint}>
          {view.storedHint}
        </ScalarHint>
      }
    >
      <ScalarFieldIsland size={size} isInvalid={isInvalid}>
        <input
          ref={inputRef}
          id={id}
          // `text` + `inputMode`, not `type="number"`: a number input reports an
          // EMPTY value for an in-progress "85." and there is no way to see what
          // was typed, which makes the draft model — and therefore decimals —
          // impossible. The spinbutton semantics are supplied explicitly below.
          type="text"
          inputMode="decimal"
          role="spinbutton"
          aria-valuenow={view.sliderValue}
          aria-valuemin={view.sliderMin}
          aria-valuemax={view.sliderMax}
          aria-valuetext={view.valueText}
          aria-invalid={isInvalid || view.number.isDraftInvalid ? true : undefined}
          aria-readonly={isReadOnly ? true : undefined}
          aria-describedby={cn(describedBy, hintId)}
          value={view.number.text}
          disabled={isDisabled}
          readOnly={isReadOnly}
          autoComplete="off"
          onChange={(event) => {
            view.number.setText(event.target.value);
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
            const steps = KEY_STEPS[event.key];
            if (steps === undefined) return;
            event.preventDefault();
            view.number.stepBy(event.shiftKey ? steps * 10 : steps);
          }}
          onBlur={() => {
            view.number.reset();
            onBlur?.();
          }}
          className={cn(FIELD_SLOT_CLASSES, PERCENT_AMOUNT_CLASSES)}
        />
        <span aria-hidden className={SCALAR_ADORNMENT_CLASSES}>
          {t('percent.symbol')}
        </span>
      </ScalarFieldIsland>

      <Slider
        className={PERCENT_SLIDER_CLASSES}
        value={view.sliderValue}
        onValueChange={view.onSliderChange}
        onRefuse={view.refuseFromSlider}
        min={view.sliderMin}
        max={view.sliderMax}
        step={step}
        disabled={isDisabled}
        isReadOnly={isReadOnly}
        aria-label={view.sliderLabel}
        aria-valuetext={view.valueText}
        // The caller's description as well as the hint: an operator who tabs
        // into the SLIDER rather than the box would otherwise never hear what
        // the field is for (found 2026-08-16 by `DynamicForm`'s per-kind
        // description table, which this control had been omitted from).
        aria-describedby={cn(describedBy, hintId)}
      />
    </ScalarShell>
  );
});
